import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NaaviLogo } from "./brand/Shapes";
import { HEADER_MENUS } from "../data/site";
import { Menu, X, ChevronDown } from "lucide-react";

/**
 * Stonehill-style horizontal header on a navy background (matches the TopBar).
 * Sits below the slim TopBar (h=36px). Sticky.
 */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-9 left-0 right-0 z-50 transition-all duration-500 bg-[#0F1B47] ${
        scrolled
          ? "border-b border-[#FDFDF9]/10 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.45)] py-2"
          : "py-3"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center" data-testid="nav-logo-link">
          <NaaviLogo height={scrolled ? 56 : 64} variant="light" />
        </Link>

        {/* Desktop nav */}
        <nav
          className="hidden lg:flex items-center gap-1 xl:gap-2"
          onMouseLeave={() => setOpenMenu(null)}
          data-testid="nav-desktop"
        >
          {HEADER_MENUS.map((m) => (
            <div
              key={m.label}
              className="relative"
              onMouseEnter={() => setOpenMenu(m.label)}
            >
              <Link
                to={m.href}
                className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-[14px] font-semibold transition-colors ${
                  openMenu === m.label
                    ? "bg-[#FDFDF9]/12 text-[#FDFDF9]"
                    : "text-[#FDFDF9] hover:bg-[#FDFDF9]/8"
                }`}
                data-testid={`nav-${slug(m.label)}`}
              >
                {m.label}
                {m.items?.length ? <ChevronDown size={13} className="opacity-80" /> : null}
              </Link>

              {/* Dropdown */}
              {m.items?.length > 0 && openMenu === m.label && (
                <div
                  className="absolute left-0 top-full pt-2 z-30"
                  data-testid={`nav-dropdown-${slug(m.label)}`}
                >
                  <div className="min-w-[260px] bg-[#FDFDF9] rounded-2xl border border-[#0F1B47]/8 shadow-[0_24px_60px_-20px_rgba(15,27,71,0.35)] p-2">
                    {m.items.map((it) => (
                      <Link
                        key={it.href + it.label}
                        to={it.href}
                        className="block px-4 py-2.5 rounded-xl hover:bg-[#F5F5F0] transition-colors"
                        data-testid={`nav-item-${slug(it.label)}`}
                      >
                        <div className="text-[14px] font-semibold text-[#0F1B47]">
                          {it.label}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/admissions#enquiry"
            className="hidden md:inline-flex items-center justify-center bg-[#C6D947] text-[#0F1B47] rounded-full px-5 py-2.5 font-semibold text-[13px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#d5e560] hover:shadow-lg"
            data-testid="nav-apply-cta"
          >
            Apply 2026 — 27
          </Link>
          <button
            className="lg:hidden inline-flex items-center justify-center p-2 text-[#FDFDF9]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0F1B47] border-t border-[#FDFDF9]/10 max-h-[calc(100vh-100px)] overflow-y-auto">
          <nav className="flex flex-col py-3 px-5">
            {HEADER_MENUS.map((m) => (
              <div key={m.label} className="border-b border-[#FDFDF9]/10">
                <button
                  className="w-full flex items-center justify-between py-3 text-[#FDFDF9] font-semibold text-[15px]"
                  onClick={() => setOpenMenu((cur) => (cur === m.label ? null : m.label))}
                  data-testid={`mobile-nav-${slug(m.label)}`}
                >
                  <span>{m.label}</span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${openMenu === m.label ? "rotate-180" : ""}`}
                  />
                </button>
                {openMenu === m.label && (
                  <div className="pb-3 pl-2">
                    <Link
                      to={m.href}
                      className="block py-2 text-[13px] font-bold tracking-[0.18em] uppercase text-[#C6D947]"
                    >
                      Overview →
                    </Link>
                    {m.items.map((it) => (
                      <Link
                        key={it.href + it.label}
                        to={it.href}
                        className="block py-2 text-sm text-[#FDFDF9]/85"
                      >
                        {it.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/admissions#enquiry"
              className="mt-4 bg-[#C6D947] text-[#0F1B47] rounded-full px-6 py-3 text-center font-semibold"
              data-testid="mobile-nav-apply-cta"
            >
              Apply 2026 — 27
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
